import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { courseService } from '../services/courseService';
import { Course, Module, ModuleContent } from '../types/course';
import AIAvatar from '../components/AIAvatar';
import AttentionMonitor from '../components/AttentionMonitor';
import SessionProgress from '../components/SessionProgress';
import QuestionModal from '../components/QuestionModal';
import { 
  ChevronLeft, 
  ChevronRight, 
  BookOpen, 
  Clock, 
  CheckCircle,
  Play,
  Pause,
  RotateCcw,
  Award,
  Target
} from 'lucide-react';

export default function TrainingSession() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState<Course | null>(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [sessionTime, setSessionTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [attentionScore, setAttentionScore] = useState(100);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [moduleProgress, setModuleProgress] = useState<{[moduleId: string]: boolean}>({});

  useEffect(() => {
    if (courseId) {
      const foundCourse = courseService.getCourseById(courseId);
      if (foundCourse) {
        setCourse(foundCourse);
        
        // Check if user can access this course
        if (user && !courseService.canAccessCourse(user.id, courseId)) {
          navigate('/');
          return;
        }

        // Initialize or load progress
        if (user) {
          let progress = courseService.getUserProgress(user.id, courseId);
          if (!progress) {
            progress = courseService.initializeCourseProgress(user.id, courseId);
          }
          
          // Set module progress
          const moduleProgressMap: {[moduleId: string]: boolean} = {};
          progress.completedModules.forEach(moduleId => {
            moduleProgressMap[moduleId] = true;
          });
          setModuleProgress(moduleProgressMap);
          
          // Set current module if there's a current one in progress
          if (progress.currentModule) {
            const moduleIndex = foundCourse.modules.findIndex(m => m.id === progress.currentModule);
            if (moduleIndex >= 0) {
              setCurrentModuleIndex(moduleIndex);
            }
          }
        }
      } else {
        navigate('/');
      }
    }
  }, [courseId, user, navigate]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && course) {
      interval = setInterval(() => {
        setSessionTime(prev => prev + 1);
        
        // Add time to course progress
        if (user && courseId) {
          courseService.addTimeSpent(user.id, courseId, 1/60); // Convert seconds to minutes
        }
        
        // Random attention checks (less frequent for better UX)
        if (Math.random() < 0.0005) { // ~33 minute average
          setShowQuestion(true);
          setIsPlaying(false);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, course, user, courseId]);

  const currentModule = course?.modules[currentModuleIndex];
  const currentContent = currentModule?.content[currentContentIndex];
  const totalModules = course?.modules.length || 0;
  const totalDuration = course?.duration ? course.duration * 60 : 0; // Convert to seconds

  const handleQuestionAnswer = (correct: boolean) => {
    const newScore = correct 
      ? Math.min(100, attentionScore + 5)
      : Math.max(0, attentionScore - 10);
    
    setAttentionScore(newScore);
    
    // Update attention score in progress
    if (user && courseId) {
      courseService.updateAttentionScore(user.id, courseId, newScore);
    }
    
    setShowQuestion(false);
    setIsPlaying(true);
  };

  const handleAvatarError = (error: string) => {
    setAvatarError(error);
    setTimeout(() => setAvatarError(null), 5000);
  };

  const goToNextContent = () => {
    if (!currentModule) return;
    
    if (currentContentIndex < currentModule.content.length - 1) {
      setCurrentContentIndex(currentContentIndex + 1);
    } else {
      // Move to next module
      goToNextModule();
    }
  };

  const goToPreviousContent = () => {
    if (currentContentIndex > 0) {
      setCurrentContentIndex(currentContentIndex - 1);
    } else if (currentModuleIndex > 0) {
      // Move to previous module
      const prevModule = course?.modules[currentModuleIndex - 1];
      if (prevModule) {
        setCurrentModuleIndex(currentModuleIndex - 1);
        setCurrentContentIndex(prevModule.content.length - 1);
      }
    }
  };

  const goToNextModule = () => {
    if (currentModuleIndex < totalModules - 1) {
      // Mark current module as completed
      if (user && courseId && currentModule) {
        courseService.completeModule(user.id, courseId, currentModule.id);
        setModuleProgress(prev => ({
          ...prev,
          [currentModule.id]: true
        }));
      }
      
      setCurrentModuleIndex(currentModuleIndex + 1);
      setCurrentContentIndex(0);
    } else {
      // Course completed
      if (user && courseId && currentModule) {
        courseService.completeModule(user.id, courseId, currentModule.id);
        // Navigate to completion page or dashboard
        navigate('/', { state: { courseCompleted: course?.title } });
      }
    }
  };

  const goToPreviousModule = () => {
    if (currentModuleIndex > 0) {
      setCurrentModuleIndex(currentModuleIndex - 1);
      setCurrentContentIndex(0);
    }
  };

  const selectModule = (moduleIndex: number) => {
    setCurrentModuleIndex(moduleIndex);
    setCurrentContentIndex(0);
    setIsPlaying(false);
  };

  const restartCurrentContent = () => {
    setIsPlaying(false);
    // Reset any content-specific state if needed
  };

  if (!course || !currentModule || !currentContent) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading course content...</p>
        </div>
      </div>
    );
  }

  const moduleCompletionPercentage = Math.round(((currentContentIndex + 1) / currentModule.content.length) * 100);
  const overallProgress = Math.round(((currentModuleIndex + (currentContentIndex + 1) / currentModule.content.length) / totalModules) * 100);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="flex h-screen">
        {/* Main Training Area */}
        <div className="flex-1 flex flex-col">
          {/* Training Header */}
          <div className="bg-gray-800 border-b border-gray-700 p-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-xl font-semibold">{course.title}</h1>
                  <p className="text-gray-400 text-sm">
                    Module {currentModuleIndex + 1}: {currentModule.title}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Content {currentContentIndex + 1} of {currentModule.content.length}: {currentContent.title}
                  </p>
                </div>
              </div>
              <SessionProgress 
                currentTime={sessionTime}
                totalTime={totalDuration}
                attentionScore={attentionScore}
              />
            </div>
            
            {/* Progress Indicators */}
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm text-gray-400">
                <span>Module Progress</span>
                <span>{moduleCompletionPercentage}%</span>
              </div>
              <div className="bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${moduleCompletionPercentage}%` }}
                />
              </div>
              
              <div className="flex justify-between text-sm text-gray-400">
                <span>Overall Progress</span>
                <span>{overallProgress}%</span>
              </div>
              <div className="bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Avatar Section */}
          <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
            <div className="max-w-4xl w-full px-8">
              <AIAvatar 
                isPlaying={isPlaying}
                onPlayPause={() => setIsPlaying(!isPlaying)}
                currentContent={currentContent.content}
                onError={handleAvatarError}
              />
              
              {/* Content Information */}
              <div className="mt-8 text-center">
                <div className="bg-gray-800 rounded-xl p-6 mb-4">
                  <h3 className="text-xl font-semibold mb-2">{currentContent.title}</h3>
                  <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {currentContent.duration} min
                    </span>
                    <span className="flex items-center">
                      <Target className="w-4 h-4 mr-1" />
                      {currentContent.type.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                
                {/* Learning Objectives */}
                {currentModule.learningObjectives.length > 0 && (
                  <div className="bg-blue-900 bg-opacity-30 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-blue-300 mb-2">Learning Objectives</h4>
                    <ul className="text-xs text-blue-200 space-y-1 text-left max-w-md mx-auto">
                      {currentModule.learningObjectives.map((objective, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          {objective}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Control Panel */}
          <div className="bg-gray-800 border-t border-gray-700 p-4">
            {/* Error Display */}
            {avatarError && (
              <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-700 rounded-lg">
                <p className="text-red-300 text-sm">
                  <strong>Avatar Error:</strong> {avatarError}
                </p>
              </div>
            )}
            
            <div className="flex justify-between items-center">
              {/* Navigation Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={goToPreviousContent}
                  disabled={currentModuleIndex === 0 && currentContentIndex === 0}
                  className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors flex items-center space-x-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                
                <button
                  onClick={restartCurrentContent}
                  className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center space-x-1"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Restart</span>
                </button>
              </div>

              {/* Play/Pause Control */}
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className={`px-8 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                  isPlaying 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                <span>{isPlaying ? 'Pause' : 'Play'} Training</span>
              </button>

              {/* Forward Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={goToNextContent}
                  className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center space-x-1"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
                
                {currentContentIndex === currentModule.content.length - 1 && (
                  <button
                    onClick={goToNextModule}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
                  >
                    {currentModuleIndex === totalModules - 1 ? (
                      <>
                        <Award className="w-4 h-4" />
                        <span>Complete Course</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Complete Module</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80 bg-gray-800 border-l border-gray-700 overflow-y-auto">
          <AttentionMonitor 
            attentionScore={attentionScore}
            isActive={isPlaying}
          />
          
          {/* Course Overview */}
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-lg font-semibold mb-2">Course Overview</h3>
            <div className="space-y-2 text-sm text-gray-400">
              <div className="flex justify-between">
                <span>Total Duration:</span>
                <span>{course.requiredHours}h</span>
              </div>
              <div className="flex justify-between">
                <span>Modules:</span>
                <span>{totalModules}</span>
              </div>
              <div className="flex justify-between">
                <span>Level:</span>
                <span className="capitalize">{course.level}</span>
              </div>
              {course.certification && (
                <div className="flex justify-between">
                  <span>Certificate:</span>
                  <span>Yes</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Module List */}
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-4">Training Modules</h3>
            <div className="space-y-2">
              {course.modules.map((module, index) => (
                <button
                  key={module.id}
                  onClick={() => selectModule(index)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    index === currentModuleIndex 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-sm">{module.title}</div>
                    {moduleProgress[module.id] && (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    )}
                  </div>
                  <div className="text-xs opacity-75 mb-2">{module.duration} minutes</div>
                  <div className="text-xs opacity-60">{module.content.length} content items</div>
                </button>
              ))}
            </div>
          </div>

          {/* Current Module Content */}
          {currentModule && (
            <div className="p-4 border-t border-gray-700">
              <h4 className="text-md font-semibold mb-3">Module Content</h4>
              <div className="space-y-2">
                {currentModule.content.map((content, index) => (
                  <div
                    key={content.id}
                    className={`p-2 rounded text-sm ${
                      index === currentContentIndex
                        ? 'bg-blue-600 text-white'
                        : index < currentContentIndex
                        ? 'bg-green-700 text-green-100'
                        : 'bg-gray-700 text-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{content.title}</span>
                      {index < currentContentIndex && (
                        <CheckCircle className="w-3 h-3" />
                      )}
                    </div>
                    <div className="text-xs opacity-75 mt-1">
                      {content.duration} min • {content.type.replace('_', ' ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Attention Question Modal */}
      {showQuestion && (
        <QuestionModal
          onAnswer={handleQuestionAnswer}
          onClose={() => setShowQuestion(false)}
        />
      )}
    </div>
  );
}